// Selebrity.js - with correct position without jumping

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import './Selebrity.css';

const SelebritySection = () => {
  const [celebrities, setCelebrities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [imageLoadError, setImageLoadError] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);
  const [nextIndex, setNextIndex] = useState(null);
  const [allImagesPreloaded, setAllImagesPreloaded] = useState(false);
  
  // Store preloaded image elements with their styles
  const [preloadedImages, setPreloadedImages] = useState({});
  
  const resizeTimeoutRef = useRef(null);
  const transitionTimeoutRef = useRef(null);

  const loadCelebrities = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('selebrity-slider')
        .select('*')
        .order('id', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        return;
      }

      if (data && data.length > 0) {
        setCelebrities(data);
      }
    } catch (err) {
      console.error('Data loading error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getImageUrl = useCallback((path) => {
    if (!path) return null;
    
    let cleanPath = path;
    if (cleanPath.startsWith('selebrity-image/')) {
      cleanPath = cleanPath.replace('selebrity-image/', '');
    }
    
    const { data } = supabase
      .storage
      .from('selebrity-image')
      .getPublicUrl(cleanPath);
    
    return data.publicUrl;
  }, []);

  // Get display name (Russian if available, otherwise English)
  const getDisplayName = useCallback((celebrity) => {
    if (!celebrity) return '';
    return celebrity.fullname && celebrity.fullname.trim() !== '' 
      ? celebrity.fullname 
      : celebrity.fullname;
  }, []);

  // Get background image path
  const getBackgroundImagePath = useCallback((celebrity) => {
    if (!celebrity) return null;
    
    const useMobile = isMobile || windowWidth <= 768;
    
    if (useMobile && celebrity.mobile_image) {
      return celebrity.mobile_image;
    } else if (celebrity.image) {
      return celebrity.image;
    }
    
    return null;
  }, [isMobile, windowWidth]);

  // Get all styles for a celebrity (with correct position)
  const getCelebrityStyles = useCallback((celebrity) => {
    if (!celebrity) return {};
    
    const useMobile = isMobile || windowWidth <= 768;
    
    // Background position - use correct position from database
    let backgroundPosition = 'center';
    if (useMobile && celebrity.mobile_background_position) {
      backgroundPosition = celebrity.mobile_background_position;
    }
    
    // Brightness
    const brightnessValue = useMobile 
      ? (celebrity.mobile_brightness || '100')
      : (celebrity.desktop_brightness || '100');
    const brightness = parseInt(brightnessValue) / 100;
    
    return {
      backgroundImage: '',
      backgroundSize: 'cover',
      backgroundPosition: backgroundPosition,
      backgroundRepeat: 'no-repeat',
      filter: `brightness(${brightness})`,
    };
  }, [isMobile, windowWidth]);

  // Preload all images with their styles
  const preloadAllImagesWithStyles = useCallback(async () => {
    if (!celebrities.length) return;
    
    const preloadPromises = celebrities.map(async (celebrity) => {
      const imagePath = getBackgroundImagePath(celebrity);
      if (!imagePath) return null;
      
      const imageUrl = getImageUrl(imagePath);
      if (!imageUrl) return null;
      
      return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          const styles = getCelebrityStyles(celebrity);
          styles.backgroundImage = `url(${imageUrl})`;
          
          setPreloadedImages(prev => ({
            ...prev,
            [celebrity.id]: {
              id: celebrity.id,
              url: imageUrl,
              styles: styles,
            }
          }));
          resolve(true);
        };
        
        img.onerror = () => {
          console.error(`Failed to load: ${celebrity.fullname}`);
          setImageLoadError(prev => ({
            ...prev,
            [celebrity.id]: true
          }));
          resolve(false);
        };
        
        img.src = imageUrl;
      });
    });
    
    await Promise.all(preloadPromises);
    setAllImagesPreloaded(true);
    
  }, [celebrities, getBackgroundImagePath, getImageUrl, getCelebrityStyles, isMobile, windowWidth]);

  // Reload styles when mobile orientation changes
  const reloadStyles = useCallback(() => {
    if (!celebrities.length || !allImagesPreloaded) return;
    
    celebrities.forEach((celebrity) => {
      const imagePath = getBackgroundImagePath(celebrity);
      if (!imagePath) return;
      
      const imageUrl = getImageUrl(imagePath);
      if (!imageUrl) return;
      
      const styles = getCelebrityStyles(celebrity);
      styles.backgroundImage = `url(${imageUrl})`;
      
      setPreloadedImages(prev => ({
        ...prev,
        [celebrity.id]: {
          ...prev[celebrity.id],
          styles: styles,
        }
      }));
    });
  }, [celebrities, allImagesPreloaded, getBackgroundImagePath, getImageUrl, getCelebrityStyles, isMobile, windowWidth]);

  // Функция для проверки необходимости скролла
  const checkScrollNeeded = useCallback(() => {
    if (window.innerWidth <= 900) return; // Только для десктопа
    
    const namesColumn = document.querySelector('.names-column');
    const scrollContainer = document.querySelector('.names-scroll-container');
    
    if (namesColumn && scrollContainer) {
      // Проверяем, переполняется ли контент
      const isScrollNeeded = scrollContainer.scrollHeight > namesColumn.clientHeight;
      
      if (isScrollNeeded) {
        scrollContainer.classList.add('scrollable');
      } else {
        scrollContainer.classList.remove('scrollable');
      }
    }
  }, []);

  // Initial preload
  useEffect(() => {
    if (celebrities.length > 0 && !allImagesPreloaded) {
      preloadAllImagesWithStyles();
    }
  }, [celebrities, preloadAllImagesWithStyles, allImagesPreloaded]);

  // Re-preload styles when mobile orientation changes
  useEffect(() => {
    if (celebrities.length > 0 && allImagesPreloaded) {
      reloadStyles();
    }
  }, [isMobile, windowWidth, reloadStyles, celebrities.length, allImagesPreloaded]);

  // Check scroll needed when celebrities load or window resizes
  useEffect(() => {
    if (celebrities.length > 0 && allImagesPreloaded) {
      // Даем время для рендера
      setTimeout(() => {
        checkScrollNeeded();
      }, 100);
    }
    
    window.addEventListener('resize', checkScrollNeeded);
    
    return () => {
      window.removeEventListener('resize', checkScrollNeeded);
    };
  }, [celebrities, allImagesPreloaded, checkScrollNeeded]);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    resizeTimeoutRef.current = setTimeout(() => {
      const width = window.innerWidth;
      const newIsMobile = width <= 768;
      
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
      setWindowWidth(width);
    }, 150);
  }, [isMobile]);

  useEffect(() => {
    loadCelebrities();
    
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [loadCelebrities]);

  useEffect(() => {
    const width = window.innerWidth;
    setWindowWidth(width);
    setIsMobile(width <= 768);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const goToSlide = useCallback((newIndex) => {
    if (isTransitioning || newIndex === selectedIndex) return;
    
    const direction = newIndex > selectedIndex ? 'from-bottom' : 'from-top';
    
    setSelectedIndex(newIndex);
    setNextIndex(newIndex);
    setSlideDirection(direction);
    setIsTransitioning(true);
    
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    transitionTimeoutRef.current = setTimeout(() => {
      setDisplayedIndex(newIndex);
      setNextIndex(null);
      setSlideDirection(null);
      setIsTransitioning(false);
    }, 700);
    
  }, [selectedIndex, isTransitioning]);

  if (loading) {
    return (
      <div className="selebrity-section">
        <div className="top-header">
          <h1 className="main-title">CELEBRITY APPEARANCES</h1>
          <p className="subtitle">We source and deliver Hollywood stars for live events, film and video productions,<br className="desktop-only" />advertising campaigns, and exclusive PR activations.</p>
        </div>
        <div className="selebrity-container">
          <div className="skeleton-wrapper">
            <div className="skeleton-names">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton-name"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (celebrities.length === 0) {
    return (
      <div className="selebrity-section">
        <div className="top-header">
          <h1 className="main-title">CELEBRITY APPEARANCES</h1>
          <p className="subtitle">We source and deliver Hollywood stars for live events, film and video productions,<br className="desktop-only" /> advertising  campaigns, and exclusive PR activations.</p>
        </div>
        <div className="selebrity-container">
          <div className="no-data">
            <p>No celebrities found</p>
          </div>
        </div>
      </div>
    );
  }

  // Get current displayed celebrity with preloaded styles
  const currentCelebrity = celebrities[displayedIndex];
  const currentPreloaded = preloadedImages[currentCelebrity?.id];
  const hasImageErrorCurrent = imageLoadError[currentCelebrity?.id];
  const displayName = getDisplayName(currentCelebrity);
  
  // Get next celebrity for animation
  const nextCelebrity = nextIndex !== null ? celebrities[nextIndex] : null;
  const nextPreloaded = nextCelebrity ? preloadedImages[nextCelebrity.id] : null;

  return (
    <div className="selebrity-section">
      <div className="top-header">
        <h1 className="main-title">CELEBRITY APPEARANCES</h1>
        <p className="subtitle">We source and deliver Hollywood stars for live events, film and video productions, <br className="desktop-only" />advertising  campaigns, and exclusive PR activations.</p>
      </div>

      <div className="selebrity-container">
        <div className="background-wrapper">
          {isMobile && !allImagesPreloaded && (
            <div className="global-loading-overlay">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading images...</p>
            </div>
          )}
          
          {/* Current image - always visible with correct position */}
          {currentCelebrity && currentPreloaded && !hasImageErrorCurrent && (
            <div 
              className="background-base"
              style={currentPreloaded.styles}
            />
          )}
          
          {/* Next image - only shown during animation, already has correct position */}
          {isTransitioning && nextCelebrity && nextPreloaded && (
            <div 
              className={`background-slide ${slideDirection === 'from-top' ? 'slide-in-from-top' : 'slide-in-from-bottom'}`}
              style={nextPreloaded.styles}
            />
          )}
          
          {(!currentCelebrity || !currentPreloaded || hasImageErrorCurrent) && !isTransitioning && (
            <div className="background-placeholder">
          
            </div>
          )}
          
          <div className="background-overlay"></div>
        </div>

        <div className="names-column">
          <div className="names-scroll-container">
            {celebrities.map((celebrity, index) => (
              <button
                key={celebrity.id}
                className={`name-button ${selectedIndex === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                disabled={isTransitioning || !allImagesPreloaded}
              >
                {getDisplayName(celebrity)}
              </button>
            ))}
              <div className="more-stars-message">
      ...and many more
    </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SelebritySection);